import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { User } from '../api/types';
import { erpGetMe, erpLogin, erpLogout, getErpToken, setErpToken } from '../api/erp';

interface ErpAuthContextValue {
  user: User | null;
  loading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const ErpAuthContext = createContext<ErpAuthContextValue | null>(null);

export function ErpAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    const token = getErpToken();
    if (!token) {
      setUser(null);
      return;
    }
    try {
      const me = await erpGetMe();
      if (me.role !== 'admin') {
        setErpToken(null);
        setUser(null);
        return;
      }
      setUser(me);
    } catch {
      setErpToken(null);
      setUser(null);
    }
  }, []);

  useEffect(() => {
    refreshUser().finally(() => setLoading(false));
  }, [refreshUser]);

  const login = useCallback(async (email: string, password: string) => {
    const result = await erpLogin(email, password);
    setUser(result.user);
  }, []);

  const logout = useCallback(async () => {
    await erpLogout();
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({
      user,
      loading,
      isAuthenticated: !!user,
      login,
      logout,
      refreshUser,
    }),
    [user, loading, login, logout, refreshUser]
  );

  return <ErpAuthContext.Provider value={value}>{children}</ErpAuthContext.Provider>;
}

export function useErpAuth(): ErpAuthContextValue {
  const ctx = useContext(ErpAuthContext);
  if (!ctx) {
    throw new Error('useErpAuth debe usarse dentro de ErpAuthProvider');
  }
  return ctx;
}
